import { useRecoilCallback, useRecoilState, useRecoilValue } from 'recoil';
import OnsaProgressBar from '../../shared_components/OnsaProgressBar/OnsaProgressBar';
import type { WizardPageComponent } from '../../config/wizardPagesConfig';
import useWizardNavigation from '../../hooks/useWizardNavigation';
import { getOnsaCurrentProductSelector } from '../../recoil/selectors/getOnsaCurrentProductSelector';
import { allCoverablesOnsaAtom } from '../../recoil/atoms/allCoverablesOnsaAtom';
import { Grid, GridItem, Wizard } from '@flos/react-ui';
import { getPostLoginQuestionsConfig } from './PostLoginQuestionStepsConfig';
import { isLoggedOnSelector } from '../../recoil/selectors/isLoggedOnSelector';
import { carReplacementsAtom } from '../../recoil/atoms/carReplacementsAtom';
import { useEffect } from 'react';
import { claimsHistorySelector } from '../../recoil/selectors/claimsHistorySelector';
import { allCoverablesAtom } from '../../recoil/atoms/allCoverablesAtom';
import { isVehicleSeniorityInheritedSelector } from '../../recoil/selectors/isVehicleSeniorityInheritedSelector';
import { onsaProgressSelector } from '../../recoil/selectors/onsaProgressSelector';

const PostLoginQuestionsPage: WizardPageComponent = () => {
    const { wizardGotoStep } = useWizardNavigation();
    const [onsaProgress, setOnsaProgress] = useRecoilState(onsaProgressSelector);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const onsaCoverables = useRecoilValue(allCoverablesOnsaAtom);
    const shoppingBasketCoverables = useRecoilValue(allCoverablesAtom);
    const isLoggedOn = useRecoilValue(isLoggedOnSelector);
    const carReplacements = useRecoilValue(carReplacementsAtom);
    const shouldSendClaimsHistory = useRecoilValue(claimsHistorySelector);
    const isVehicleSeniorityInherited = useRecoilValue(isVehicleSeniorityInheritedSelector);

    const postLoginSteps = getPostLoginQuestionsConfig(
        currentProduct,
        shoppingBasketCoverables,
        onsaCoverables,
        isLoggedOn,
        !!carReplacements,
        isVehicleSeniorityInherited,
        shouldSendClaimsHistory
    ).filter((step) => !step.disabled);
    const currentStep = onsaProgress.currentStep + postLoginSteps.length - onsaProgress.totalSteps;

    useEffect(() => {
        if (postLoginSteps.length === 0) wizardGotoStep('CoveragePackagePage');
        else {
            setOnsaProgress((prevStep) => ({
                ...prevStep,
                currentStep: onsaProgress.totalSteps - postLoginSteps.length + 1
            }));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const goNext = useRecoilCallback(
        ({ snapshot }) =>
            () => {
                void snapshot.getPromise(allCoverablesOnsaAtom).then((allCoverables) => {
                    const postloginStep = getPostLoginQuestionsConfig(
                        currentProduct,
                        shoppingBasketCoverables,
                        allCoverables,
                        isLoggedOn,
                        !!carReplacements,
                        isVehicleSeniorityInherited,
                        shouldSendClaimsHistory
                    ).filter((step) => !step.disabled);
                    if (currentStep === postloginStep.length) {
                        wizardGotoStep(onsaProgress.nextStep);
                    }
                    setOnsaProgress((prevStep) => ({
                        ...prevStep,
                        currentStep: prevStep.currentStep + 1
                    }));
                });
            },
        [allCoverablesOnsaAtom, currentStep, currentProduct]
    );

    const goPrevious = (): void => {
        if (currentStep === 1) {
            wizardGotoStep(onsaProgress.prevStep);
        }
        setOnsaProgress((prevStep) => ({
            ...prevStep,
            currentStep: prevStep.currentStep - 1
        }));
    };

    return (
        <Wizard.Container currentStep={currentStep}>
            {postLoginSteps.map((step) => {
                const { id, Component } = step;
                return (
                    <Wizard.Pane key={id} hideProgress>
                        <Grid>
                            <GridItem span={8} colStart={3}>
                                <OnsaProgressBar />
                                <Component goNext={goNext} goPrevious={goPrevious} />
                            </GridItem>
                        </Grid>
                    </Wizard.Pane>
                );
            })}
        </Wizard.Container>
    );
};

export default PostLoginQuestionsPage;
